import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

interface DropdownOption {
  id: string;
  label: string;
  value: string;
}

interface EntityTypeDropdownProps {
  options: DropdownOption[];
  selected: string;
  onSelect: (value: string) => void;
  className?: string;
  dropdownWidth?: number;
  dropdownHeight?: number;
  dropdownPosition?: 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';
}

/**
 * EntityTypeDropdown - Custom dropdown for entity type selection with configurable positioning
 *
 * Features:
 * - Custom positioning control (top/bottom, left/right)
 * - Configurable width and height
 * - Matches GraphExplorer styling
 * - Click outside to close
 * - Keyboard navigation support
 */
const EntityTypeDropdownComponent: React.FC<EntityTypeDropdownProps> = ({
  options,
  selected,
  onSelect,
  className,
  dropdownWidth = 200,
  dropdownHeight = 300,
  dropdownPosition = 'bottom-left',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find(opt => opt.value === selected);

  // Filter out the currently selected option from the dropdown list
  const availableOptions = options.filter(opt => opt.value !== selected);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (option: DropdownOption) => {
    onSelect(option.value);
    setIsOpen(false);
  };

  // Memoize position classes to prevent recalculation
  const positionClasses = useMemo(() => {
    switch (dropdownPosition) {
      case 'bottom-left':
        return 'top-full left-0 mt-1';
      case 'bottom-right':
        return 'top-full right-0 mt-1';
      case 'top-left':
        return 'bottom-full left-0 mb-1';
      case 'top-right':
        return 'bottom-full right-0 mb-1';
      default:
        return 'top-full left-0 mt-1';
    }
  }, [dropdownPosition]);

  // Memoize dropdown style object to prevent recreation
  const dropdownStyle = useMemo(
    () => ({
      width: `${dropdownWidth}px`,
      maxHeight: `${dropdownHeight}px`,
    }),
    [dropdownWidth, dropdownHeight]
  );

  return (
    <div className={twMerge('relative', className)}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="bg-chariot-brand-primary border-chariot-brand-primary hover:bg-chariot-brand-secondary flex items-center gap-2 rounded px-3 py-1.5 text-xs font-bold text-white transition-colors"
      >
        <span>{selectedOption?.label || 'Select entity type'}</span>
        <ChevronDownIcon
          className={twMerge(
            'h-4 w-4 transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div
          ref={dropdownRef}
          className={twMerge(
            'bg-chariot-background-primary border-chariot-stroke absolute z-50 overflow-hidden rounded border shadow-lg',
            positionClasses
          )}
          style={dropdownStyle}
        >
          <div className="scrollbar-thin scrollbar-track-chariot-background-secondary scrollbar-thumb-chariot-stroke max-h-full overflow-y-auto">
            {availableOptions.map(option => (
              <button
                key={option.id}
                onClick={() => handleSelect(option)}
                className="hover:bg-chariot-background-secondary text-chariot-text-primary w-full px-4 py-2.5 text-left text-sm transition-colors"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Memoized EntityTypeDropdown to prevent unnecessary re-renders
 */
export const EntityTypeDropdown = memo(
  EntityTypeDropdownComponent,
  (prevProps, nextProps) => {
    return (
      prevProps.selected === nextProps.selected &&
      prevProps.options.length === nextProps.options.length &&
      prevProps.dropdownWidth === nextProps.dropdownWidth &&
      prevProps.dropdownHeight === nextProps.dropdownHeight &&
      prevProps.dropdownPosition === nextProps.dropdownPosition &&
      prevProps.onSelect === nextProps.onSelect
    );
  }
);
